import { closeDialog } from '@/lib/dialogMotion'
import { useSearchParams } from 'react-router-dom'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Layout from '@/components/Layout'
import { FilterSelect } from '@/components/FilterDropdown'
import { usersApi, type User, type Workspace } from '@/api/users'
import { errorMessage } from '@/api/client'
import { useAuthStore } from '@/store/auth'

function formatPhone(phone: string) {
  // Current phone identities use the mainland China country code.
  return phone.replace(/^(\+86)(1\d{10})$/, '$1 $2')
}

const statuses: Record<string, string> = {
  active: '正常',
  disabled: '已禁用',
  pending_email: '待验证',
  deletion_pending: '注销中',
}
const field = 'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2'
type Action = 'status' | 'recharge' | 'reset' | 'unbindWechat' | 'delete'
const names = {
  status: '变更账号状态',
  recharge: '手动充值',
  reset: '重置密码',
  unbindWechat: '微信解绑',
  delete: '永久删除用户',
}

function ActionDialog({
  user,
  action,
  workspaces,
  close,
}: {
  user: User
  action: Action
  workspaces: Workspace[]
  close: (message?: string) => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [workspace, setWorkspace] = useState(
    workspaces.length === 1 ? workspaces[0].id : '',
  )
  const selectedWorkspace = workspaces.find((w) => w.id === workspace)
  const teamGrant = selectedWorkspace?.kind === 'team'
  const [points, setPoints] = useState('')
  const [confirmLarge, setConfirmLarge] = useState(false)
  const admin = useAuthStore((s) => s.admin)
  useEffect(() => setConfirmLarge(false), [points])
  const [confirmID, setConfirmID] = useState('')
  const [password, setPassword] = useState('')
  const [reason, setReason] = useState('')
  const [review, setReview] = useState(false)
  const request = useRef<{ payload: string; key: string }>()
  const cache = useQueryClient()
  const nextStatus = user.status === 'active' ? 'disabled' : 'active'
  const mutation = useMutation({
    mutationFn: async () => {
      if (action === 'delete') return usersApi.delete(user.id, reason.trim(), confirmID)
      if (action === 'unbindWechat') return usersApi.unbindWechat(user.id, reason.trim())
      if (action === 'status')
        return usersApi.status(user.id, nextStatus, reason.trim())
      if (action === 'reset')
        return usersApi.reset(user.id, password, reason.trim())
      const payload = JSON.stringify([
        user.id,
        workspace,
        Number(points),
        reason.trim(),
        teamGrant ? 'team_grant' : 'manual_recharge',
      ])
      if (request.current?.payload !== payload)
        request.current = { payload, key: crypto.randomUUID() }
      return usersApi.recharge(
        user.id,
        workspace,
        Number(points),
        reason.trim(),
        request.current.key,
        confirmLarge,
        teamGrant ? 'team_grant' : 'manual_recharge',
      )
    },
    onSuccess: async () => {
      await Promise.all([
        cache.invalidateQueries({ queryKey: ['users'] }),
        ...(action === 'delete' ? [] : [cache.invalidateQueries({ queryKey: ['user', user.id] })]),
        cache.invalidateQueries({ queryKey: ['audit'] }),
      ])
      close(
        action === 'delete' ? '用户已永久删除。' : action === 'unbindWechat' ? '微信已解绑，用户已有会话全部失效。' : action === 'recharge'
          ? teamGrant ? '团队赠点成功，积分已入账。' : '充值成功，积分已入账，已有欠费优先结清。'
          : action === 'reset'
            ? '密码已重置，用户已有会话全部失效。'
            : '账号状态已更新。',
      )
    },
  })
  useEffect(() => {
    dialog.current?.showModal()
  }, [])
  function submit(e: FormEvent) {
    e.preventDefault()
    if (!review) setReview(true)
    else mutation.mutate()
  }
  return (
    <dialog
      ref={dialog}
      aria-labelledby="action-title"
      onCancel={(e) => {
        e.preventDefault()
        if (!mutation.isPending) close()
      }}
      className="w-[calc(100%-2rem)] max-w-lg rounded-2xl border border-slate-200 p-0 shadow-2xl backdrop:bg-slate-900/40 backdrop:backdrop-blur-sm"
    >
      <form onSubmit={submit} className="p-6">
        <h2 id="action-title" className="text-xl font-semibold">
          {action === 'recharge' && teamGrant ? '团队人工赠点' : names[action]}
        </h2>
        <p className="mb-5 mt-1 text-sm text-slate-500">
          {user.display_name} · {user.ppid}
        </p>
        {review ? (
          <div className="space-y-3 rounded-xl bg-slate-50 p-4 text-sm">
            <p className="font-medium">请核对本次操作</p>
            <p>
              目标用户：{user.display_name} · {user.id} ·{' '}
              {formatPhone(user.phone) || '未绑定手机号'}
            </p>
            <p>操作者：{admin?.username}</p>
            {action === 'recharge' && (
              <>
                <p>
                  工作区：{selectedWorkspace?.name}
                </p>
                <p>
                  {teamGrant ? '人工赠点' : '充值积分'}：<strong>{Number(points).toLocaleString()}</strong>
                </p>
                <p>
                  积分进入该工作区的购买积分，已有欠费优先结清；订阅积分与预留积分不增加。
                </p>
                {teamGrant && <p className="text-amber-800">此操作不对应支付订单，将以管理员赠点单独记录并审计。</p>}
                {Number(points) >= 1000000 && (
                  <label className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-amber-900">
                    <input
                      type="checkbox"
                      required
                      checked={confirmLarge}
                      onChange={(e) => setConfirmLarge(e.target.checked)}
                    />
                    本次为大额充值（100万积分及以上），我已核对目标用户和充值数量。
                  </label>
                )}
              </>
            )}
            {action === 'status' && (
              <p>
                将账号设为：<strong>{statuses[nextStatus]}</strong>
                {nextStatus === 'disabled' && '，并撤销已有登录会话。'}
              </p>
            )}
            {action === 'reset' && (
              <p>将设置新密码，并撤销该用户所有登录会话。</p>
            )}
            {action === 'unbindWechat' && <p>将解除微信绑定并退出该用户所有登录会话。用户需通过已验证手机号或邮箱重新登录。</p>}
            {action === 'delete' && <p className="text-red-700">永久删除账号、登录凭据和绑定信息，无法恢复。删除操作的审计记录会保留。</p>}
            <p className="break-words">原因：{reason}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {action === 'delete' && <div className="space-y-3 rounded-lg bg-red-50 p-3 text-sm text-red-700"><p>永久删除不可恢复。有工作区、创作内容或积分业务记录的账号，需要先完成数据清理或转移；系统会拒绝直接删除。</p><label className="block">输入完整用户 ID 确认：<code className="block break-all">{user.id}</code><input aria-label="确认删除用户 ID" required className={field} value={confirmID} onChange={e => setConfirmID(e.target.value)} autoComplete="off" /></label></div>}
            {action === 'unbindWechat' && <p className="text-sm">解绑前请确认用户已有可用的手机号或邮箱。此操作会撤销所有登录会话。</p>}
            {action === 'recharge' && (
              <>
                <label className="block text-sm">
                  目标工作区
                  <select
                    required
                    className={field}
                    value={workspace}
                    onChange={(e) => setWorkspace(e.target.value)}
                  >
                    <option value="">请选择工作区</option>
                    {workspaces.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}{w.kind === 'team' ? '（团队）' : ''}（可用 {w.available_points.toLocaleString()}{' '}
                        积分）
                      </option>
                    ))}
                  </select>
                </label>
                <div
                  className="flex gap-2"
                  role="group"
                  aria-label="快捷充值积分"
                >
                  {[500000, 1000000, 2000000].map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      aria-pressed={Number(points) === amount}
                      className={
                        'flex-1 rounded-xl border px-3 py-3 text-sm font-semibold transition ' +
                        (Number(points) === amount
                          ? 'border-brand-600 bg-brand-50 text-brand-700'
                          : 'border-slate-200 hover:border-brand-400 hover:bg-slate-50')
                      }
                      onClick={() => setPoints(String(amount))}
                    >
                      {amount / 10000}万
                    </button>
                  ))}
                </div>
                <label className="block text-sm">
                  {teamGrant ? '赠送积分' : '充值积分'}
                  <input
                    required
                    type="number"
                    min="1"
                    max="100000000"
                    step="1"
                    className={field}
                    value={points}
                    onChange={(e) => setPoints(e.target.value)}
                  />
                </label>
              </>
            )}
            {action === 'reset' && (
              <label className="block text-sm">
                新密码
                <input
                  required
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  className={field}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <span className="mt-1 block text-xs text-slate-500">
                  12–128 位，通过安全渠道交给用户。
                </span>
              </label>
            )}
            {action === 'status' && (
              <p className="text-sm">
                本次将账号设为「{statuses[nextStatus]}」。
              </p>
            )}
            <label className="block text-sm">
              操作原因
              <textarea
                required
                className={field}
                rows={3}
                value={reason}
                onChange={(e) => {
                  const value = Array.from(e.target.value)
                    .slice(0, 500)
                    .join('')
                  setReason(value)
                  e.target.setCustomValidity(
                    Array.from(value.trim()).length < 2
                      ? '操作原因至少填写2个字符'
                      : '',
                  )
                }}
              />
            </label>
          </div>
        )}
        {mutation.isError && (
          <p
            role="alert"
            className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700"
          >
            {errorMessage(mutation.error)}
          </p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            disabled={mutation.isPending}
            className="rounded-lg border px-4 py-2 disabled:opacity-50"
            onClick={() => close()}
          >
            取消
          </button>
          {review && (
            <button
              type="button"
              disabled={mutation.isPending}
              className="rounded-lg border px-4 py-2 disabled:opacity-50"
              onClick={() => {
                setReview(false); setConfirmLarge(false)
                mutation.reset()
              }}
            >
              返回修改
            </button>
          )}
          <button
            disabled={
              mutation.isPending || Array.from(reason.trim()).length < 2 || (action === 'delete' && confirmID !== user.id)
            }
            className="rounded-lg bg-brand-600 px-4 py-2 text-white disabled:opacity-50"
          >
            {mutation.isPending ? '提交中…' : review ? '确认执行' : '核对操作'}
          </button>
        </div>
      </form>
    </dialog>
  )
}

function Detail({ id, onDeleted }: { id: string; onDeleted: () => void }) {
  const query = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersApi.detail(id),
  })
  const admin = useAuthStore((s) => s.admin)
  const [action, setAction] = useState<Action | null>(null)
  const [message, setMessage] = useState('')
  if (query.isPending)
    return (
      <section className="p-6">
        加载用户详情…
      </section>
    )
  if (query.isError)
    return (
      <section
        className="p-6"
        role="alert"
      >
        {errorMessage(query.error)}{' '}
        <button onClick={() => query.refetch()}>重试</button>
      </section>
    )
  const { user, workspaces, consumption } = query.data
  const manageable = ['active', 'disabled'].includes(user.status)
  return (
    <section className="p-6">
      <div className="flex justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">{user.display_name}</h2>
          <p className="mt-1 text-sm text-slate-500">
            {user.ppid} · {user.username}
          </p>
        </div>

      </div>
      <div className="my-4 space-y-2 text-sm">
        <p>状态：{statuses[user.status] || user.status}</p>
        <p>手机号：{formatPhone(user.phone) || '未绑定'}</p>
        <p>
          最近登录：
          {user.last_login_at
            ? new Date(user.last_login_at).toLocaleString()
            : '暂无记录'}
        </p>
        <p>累计消费：{user.total_consumed_points.toLocaleString()} 积分</p>
        <p className="break-all">用户 ID：{user.id}</p>
        <p>注册时间：{new Date(user.created_at).toLocaleString()}</p>
      </div>
      {message && (
        <p
          role="status"
          className="my-4 rounded-lg bg-green-50 p-3 text-sm text-green-800"
        >
          {message}
        </p>
      )}
      <h3 className="mb-3 font-medium">拥有的工作区</h3>
      <div className="space-y-3">
        {workspaces.map((w) => (
          <div key={w.id} className="rounded-lg bg-slate-50 p-3 text-sm">
            <p className="font-medium">
              {w.name}{' '}
              <span className="font-normal text-slate-500">
                {w.kind === 'personal' ? '个人' : '团队'}
              </span>
            </p>
            <p className="mt-2">
              可用积分{' '}
              <strong className="text-brand-700">
                {w.available_points.toLocaleString()}
              </strong>
            </p>
            <p className="mt-1 text-xs text-slate-500">
              购买 {w.purchased_points.toLocaleString()} · 订阅{' '}
              {w.subscription_points.toLocaleString()} · 预留{' '}
              {w.reserved_points.toLocaleString()} · 欠费{' '}
              {w.outstanding_points.toLocaleString()}
            </p>
          </div>
        ))}
      </div>
      {!workspaces.length && (
        <p className="text-sm text-slate-500">
          该用户暂无拥有的工作区，不能手动充值。
        </p>
      )}
      <div className="mt-5 flex flex-wrap gap-2 text-sm">
        {admin?.role !== 'finance' && manageable && (
          <button
            className="rounded-lg border px-3 py-2"
            onClick={() => setAction('status')}
          >
            {user.status === 'active' ? '禁用账号' : '启用账号'}
          </button>
        )}
        {admin?.role !== 'operator' &&
          user.status === 'active' &&
          workspaces.length > 0 && (
            <button
              className="rounded-lg bg-brand-600 px-3 py-2 text-white"
              onClick={() => setAction('recharge')}
            >
              手动充值
            </button>
          )}
        {admin?.role === 'super_admin' && manageable && (
          <button
            className="rounded-lg border px-3 py-2"
            onClick={() => setAction('reset')}
          >
            重置密码
          </button>
        )}
      </div>
      {admin?.role === 'super_admin' && manageable && <div className="mt-3"><button disabled={!user.wechat_bound} className="rounded-lg border px-3 py-2 text-sm text-red-700 disabled:opacity-40" onClick={() => setAction('unbindWechat')}>{user.wechat_bound ? '微信解绑' : '微信未绑定'}</button></div>}
      {admin?.role === 'super_admin' && <button className="mt-3 rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700" onClick={() => setAction('delete')}>永久删除用户</button>}
      <section className="mt-6 border-t pt-5">
        <h3 className="font-medium">近30天消费</h3>
        <p className="my-2 text-sm">
          共 {consumption.count} 笔扣费 · {consumption.points.toLocaleString()}{' '}
          积分
        </p>
        <p className="mb-3 text-xs text-slate-500">
          按该用户发起的账本扣费统计，不抵扣退款，不含未扣欠费。展示最近20笔。
        </p>
        {consumption.items.length ? (
          <ul className="space-y-3 text-xs">
            {consumption.items.map((item) => (
              <li key={item.id} className="rounded-lg bg-slate-50 p-3">
                <p>
                  {new Date(item.created_at).toLocaleString()} ·{' '}
                  {item.points.toLocaleString()} 积分
                </p>
                <p className="mt-1 break-all text-slate-500">
                  {item.source_type} · {item.source_id}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">近30天暂无消费</p>
        )}
      </section>
      {action && (
        <ActionDialog
          user={user}
          action={action}
          workspaces={workspaces}
          close={(msg) => {
            setAction(null)
            if (msg && action === 'delete') { onDeleted(); return }
            if (msg) setMessage(msg)
          }}
        />
      )}
    </section>
  )
}

function UserDetailDialog({ id, close }: { id: string; close: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const element = dialog.current
    element?.showModal()

  }, [])
  return (
    <dialog
      ref={dialog}
      aria-labelledby="user-detail-title"
      className="row-detail-dialog fixed m-auto max-h-[85dvh] w-[calc(100%-2rem)] max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 shadow-xl backdrop:bg-slate-900/40"
      onCancel={(event) => { if (event.target === event.currentTarget) { event.preventDefault(); void closeDialog(dialog.current) } }}
      onClose={(event) => { if (event.target === event.currentTarget) close() }}
      onClick={(event) => { if (event.target === event.currentTarget) void closeDialog(dialog.current) }}
    >
      <div className="flex max-h-[85dvh] flex-col" onClick={(event) => event.stopPropagation()}>
        <div className="flex shrink-0 items-center justify-between border-b px-6 py-4">
          <h2 id="user-detail-title" className="text-lg font-semibold">用户详情</h2>
          <button type="button" autoFocus className="rounded-lg border px-3 py-1 text-sm" onClick={() => void closeDialog(dialog.current)}>关闭</button>
        </div>
        <div className="min-h-0 overflow-auto overscroll-contain"><Detail id={id} onDeleted={close} /></div>
      </div>
    </dialog>
  )
}

export default function UsersPage() {
  const cache = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [input, setInput] = useState(params.get('search') || '')
  const [search, setSearch] = useState(params.get('search') || '')
  const [status, setStatus] = useState('')
  const [registered, setRegistered] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<string | null>(null)
  useEffect(() => { const value = params.get('search') || ''; setInput(value); setSearch(value); setPage(1) }, [params])
  const query = useQuery({
    queryKey: ['users', page, search, status, registered],
    queryFn: () => usersApi.list(page, search, status, registered),
  })
  return (
    <Layout>
      <h1 className="text-2xl font-semibold">用户管理</h1>
      <p className="mb-6 mt-2 text-sm text-slate-500">
        查询用户与积分，管理账号状态、个人充值和团队赠点。
      </p>
      <form
        className="mb-5 flex flex-wrap gap-3"
        onSubmit={(e) => {
          e.preventDefault()
          setSearch(input.trim())
          setPage(1)
        }}
      >
        <input
          className="h-10 w-full min-w-0 rounded-lg border px-3 text-sm sm:w-[480px] sm:max-w-full"
          aria-label="搜索用户"
          placeholder="搜索用户ID、手机号、用户名、昵称或 PPID"
          maxLength={100}
          value={input}
          onChange={(e) => {
            const value = e.target.value
            setInput(value)
            if (!value.trim()) {
              setSearch('')
              setPage(1)
              void cache.invalidateQueries({ queryKey: ['users'] })
            }
          }}
        />
        <FilterSelect
          hideLabel
          label="账号状态"
          value={status}
          options={[["", "全部状态"], ...Object.entries(statuses)]}
          onChange={(value) => { setStatus(value); setPage(1) }}
        />
        <FilterSelect
          hideLabel
          label="注册时间"
          value={registered}
          options={[["", "全部注册时间"], ["today", "今天"], ["7d", "近7天"], ["30d", "近30天"]]}
          onChange={(value) => { setRegistered(value); setPage(1) }}
        />
        <button className="h-10 rounded-lg bg-brand-600 px-5 text-sm text-white">
          搜索
        </button>
        <button
          type="button"
          className="h-10 rounded-lg border border-slate-200 bg-white px-5 text-sm text-slate-600 hover:bg-slate-50"
          onClick={() => {
            setInput('')
            setSearch('')
            setStatus('')
            setRegistered('')
            setPage(1)
            if (params.has('search')) {
              const next = new URLSearchParams(params)
              next.delete('search')
              setParams(next, { replace: true })
            }
            void cache.invalidateQueries({ queryKey: ['users'] })
          }}
        >
          重置
        </button>
      </form>
      <div className="flex min-h-0 flex-1 flex-col gap-5 xl:flex-row">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {query.isError && (
            <p role="alert" className="mb-4 text-red-700">
              {errorMessage(query.error)}{' '}
              <button onClick={() => query.refetch()}>重试</button>
            </p>
          )}
          {query.isPending ? (
            <p className="p-8">正在加载用户…</p>
          ) : (
            query.data && (
              <>
                <div className="min-h-64 flex-1 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 bg-slate-100 text-slate-600">
                      <tr>
                        {['用户', '微信绑定状态', 'PPID', '状态', '注册时间', '操作'].map(
                          (s) => (
                            <th
                              key={s}
                              className={
                                'whitespace-nowrap p-3 ' +
                                (s === 'PPID'
                                  ? 'hidden sm:table-cell'
                                  : s === '注册时间'
                                    ? 'hidden lg:table-cell'
                                    : '')
                              }
                            >
                              {s}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {query.data.items.map((u) => (
                        <tr
                          key={u.id}
                          className={
                            'border-t ' +
                            (selected === u.id
                              ? 'bg-brand-50'
                              : 'hover:bg-slate-50')
                          }
                        >
                          <td className="p-3">
                            <p className="font-medium">{u.display_name}</p>
                            <p className="text-xs text-slate-500">
                              {formatPhone(u.phone) || '未绑定手机号'} · 累计{' '}
                              {u.total_consumed_points.toLocaleString()} 积分
                            </p>
                            <p className="text-xs text-slate-500">
                              {u.username}
                            </p>
                            <p className="mt-1 font-mono text-xs text-slate-400 sm:hidden">
                              {u.ppid}
                            </p>
                          </td>
                          <td className="whitespace-nowrap p-3">
                            <span className={'rounded-full px-2 py-1 text-xs ' + (u.wechat_bound ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600')}>
                              {u.wechat_bound ? '已绑定' : '未绑定'}
                            </span>
                          </td>
                          <td className="hidden whitespace-nowrap p-3 font-mono text-xs sm:table-cell">
                            {u.ppid}
                          </td>
                          <td className="whitespace-nowrap p-3">
                            <span
                              className={
                                'rounded-full px-2 py-1 text-xs ' +
                                (u.status === 'active'
                                  ? 'bg-green-50 text-green-700'
                                  : 'bg-slate-100 text-slate-600')
                              }
                            >
                              {statuses[u.status] || u.status}
                            </span>
                          </td>
                          <td className="hidden whitespace-nowrap p-3 text-slate-500 lg:table-cell">
                            {new Date(u.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-3">
                            <button
                              className="whitespace-nowrap text-brand-600"
                              onClick={(event) => {
 const rect = event.currentTarget.closest('tr')!.getBoundingClientRect()
 document.documentElement.style.setProperty('--detail-origin-x', (rect.left + rect.width / 2 - window.innerWidth / 2) + 'px')
 document.documentElement.style.setProperty('--detail-origin-y', (rect.top + rect.height / 2 - window.innerHeight / 2) + 'px')
 setSelected(u.id)
 }}
                            >
                              查看详情
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!query.data.items.length && (
                    <p className="p-10 text-center text-slate-500">
                      没有符合条件的用户
                    </p>
                  )}
                </div>
                <div className="mt-4 flex shrink-0 justify-between text-sm text-slate-600">
                  <span>
                    共 {query.data.total} 位 · 第 {page} 页
                  </span>
                  <div className="flex gap-4">
                    <button
                      className="disabled:opacity-30"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      上一页
                    </button>
                    <button
                      className="disabled:opacity-30"
                      disabled={page * 20 >= query.data.total}
                      onClick={() => setPage(page + 1)}
                    >
                      下一页
                    </button>
                  </div>
                </div>
              </>
            )
          )}
        </div>
        {selected && (
          <UserDetailDialog key={selected}
            id={selected}
            close={() => setSelected(null)}
          />
        )}
      </div>
    </Layout>
  )
}




